import { useFeatureFlag } from "@src/backpack-console/featureFlags";
import { CERTIFICATIONS_PATHS } from "@src/backpack-console/pages/Certifications/CertificationsBreadcrumbs";
import { EQUIPMENT_PATHS } from "@src/backpack-console/pages/Equipment/EquipmentBreadcrumbs";
import { PROJECT_PATHS } from "@src/backpack-console/pages/Projects/utils";
import { useIsPropertyPageEnabled } from "@src/backpack-console/pages/Property/Multifamily/utils";
import { LeftNavItem } from "@src/straps/base/navigation/LeftNav";
import { ComponentPropsWithoutRef } from "react";
import { generatePath } from "react-router-dom";
import { BuildingSelectionDocumentsItem } from "./BuildingSelectionDocumentsItem";
import { BuildingSelectionSubItem } from "./BuildingSelectionSubItem";

interface BuildingSelectionItemProps
  extends ComponentPropsWithoutRef<typeof LeftNavItem> {
  siteId: number;
  omitParentComponent?: boolean;
}
export function BuildingSelectionItem({
  siteId,
  omitParentComponent,
  ...props
}: Readonly<BuildingSelectionItemProps>) {
  const isDecarbPlanningEnabled =
    useFeatureFlag("decarb-planning", "off") !== "off";

  const {
    enabled: isPropertyPageEnabled,
    isSpacesEnabled,
    isUnitTypesEnabled,
    isPropertyUseEnabled,
  } = useIsPropertyPageEnabled({ siteId: String(siteId) });

  const subItems = (
    <>
      <BuildingSelectionSubItem
        title="Overview"
        subPath="overview"
        iconName="overview"
        siteId={siteId}
      >
        <BuildingSelectionSubItem
          key="building-info"
          title="Property Information"
          subPath="overview/building-info"
          siteId={siteId}
        />
        <BuildingSelectionSubItem
          key="vendor-list"
          title="Vendor List"
          subPath="overview/vendor-list"
          siteId={siteId}
        />
      </BuildingSelectionSubItem>
      <BuildingSelectionSubItem
        title="Utilities"
        subPath="utilities"
        iconName="utilities"
        siteId={siteId}
      />
      {isPropertyPageEnabled && (
        <BuildingSelectionSubItem
          title="Property"
          subPath="property"
          iconName="property-uses"
          siteId={siteId}
        >
          <BuildingSelectionSubItem
            title="Site Overview"
            subPath="property/site-overview"
            siteId={siteId}
          />
          {isSpacesEnabled && (
            <BuildingSelectionSubItem
              title="Spaces"
              subPath="property/spaces"
              siteId={siteId}
            />
          )}
          {isUnitTypesEnabled && (
            <BuildingSelectionSubItem
              title="Unit Type"
              subPath="property/unit-type"
              siteId={siteId}
            />
          )}
          {isPropertyUseEnabled && (
            <BuildingSelectionSubItem
              title="Property Use"
              subPath="property/property-use"
              siteId={siteId}
            />
          )}
        </BuildingSelectionSubItem>
      )}
      <BuildingSelectionSubItem
        title="Submissions"
        subPath="submissions"
        iconName="submissions"
        siteId={siteId}
      >
        <BuildingSelectionSubItem
          title="Energy Star"
          subPath="submissions/energy-star"
          siteId={siteId}
        />
        <BuildingSelectionSubItem
          title="Compliance"
          subPath="submissions/compliance"
          siteId={siteId}
        />
        <LeftNavItem
          title="Certifications"
          path={generatePath(CERTIFICATIONS_PATHS.SITE_PAGE, { siteId })}
        />
      </BuildingSelectionSubItem>
      <BuildingSelectionDocumentsItem siteId={siteId} />
      {isDecarbPlanningEnabled ? (
        <LeftNavItem
          title="Projects"
          path={generatePath(PROJECT_PATHS.BASE_SITE_PATH, { siteId })}
          iconName="project"
        >
          <LeftNavItem
            title="All Projects"
            path={generatePath(PROJECT_PATHS.SITE_PAGE, { siteId })}
            iconName="capital-plan"
          />
          <LeftNavItem
            exact
            title="Decarb Planning"
            path={generatePath(PROJECT_PATHS.DECARB_PAGE, { siteId })}
            iconName="emissions"
          />
        </LeftNavItem>
      ) : (
        <LeftNavItem
          title="Projects"
          path={generatePath(PROJECT_PATHS.SITE_PAGE, { siteId })}
          iconName="project"
        />
      )}

      <BuildingSelectionSubItem
        title="Operations"
        subPath="operations"
        iconName="operations"
        siteId={siteId}
      />
      <LeftNavItem
        title="Equipment"
        path={generatePath(EQUIPMENT_PATHS.SITE_PAGE, { siteId })}
        iconName="equipment"
      />
    </>
  );

  if (omitParentComponent) {
    return subItems;
  }

  return (
    <LeftNavItem {...props} collapseSiblingsOnExpand>
      {subItems.props.children}
    </LeftNavItem>
  );
}
